import { ArrowDown, ArrowUp } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import type { Register } from '../query/types'
import { cleanColumns } from '../query/urlState'

/** The columns the picker starts from: those given that exist, else the defaults; the fixed column is always first. */
function draftOf(columns: readonly string[], register: Register): string[] {
  const own = cleanColumns(columns, register)
  return own.length > 0 ? own : cleanColumns(register.default_columns, register)
}

/** The controls of a column's row: its checkbox and its two arrows. */
type Control = 'check' | 'up' | 'down'

/**
 * "Kolommen" (design §4.2): which columns the table shows and in what order. A checkbox adds a
 * column at the end or takes it away, ↑/↓ move it; the name is fixed in front. Nothing changes
 * until "Toepassen", and each opening starts from the columns the table has.
 */
export function ColumnPickerSheet({
  open,
  onOpenChange,
  register,
  columns,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  register: Register
  /** The columns the table shows now, in order. */
  columns: readonly string[]
  onApply: (columns: string[]) => void
}) {
  const [draft, setDraft] = useState<string[]>(() => draftOf(columns, register))
  // Opening it again starts over from the columns of the table, not from what was left in the sheet.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setDraft(draftOf(columns, register))
  }

  const shown = register.properties.filter((property) => property.column !== null)
  // The fixed columns sit in front of every clean draft: nothing moves above them.
  const fixedCount = shown.filter((property) => property.column?.fixed).length
  const others = shown.filter((property) => !draft.includes(property.key))

  // A move or a tick gives the row of that column another place, and React re-inserts it there,
  // which takes the focus away: a keyboard user would have to tab in from the top again. The
  // control that was used gets the focus back. At an end the arrow that was used is disabled, so
  // the other arrow of the same column takes it.
  const controls = useRef(new Map<string, HTMLElement>())
  const refocus = useRef<{ key: string; control: Control } | null>(null)
  useEffect(() => {
    const wanted = refocus.current
    if (!wanted) return
    refocus.current = null
    const at = draft.indexOf(wanted.key)
    const control = wanted.control === 'up' && at <= fixedCount ? 'down' : wanted.control === 'down' && at === draft.length - 1 ? 'up' : wanted.control
    controls.current.get(`${wanted.key}:${control}`)?.focus()
  }, [draft, fixedCount])

  function controlRef(key: string, control: Control) {
    return (node: HTMLElement | null) => {
      if (node) controls.current.set(`${key}:${control}`, node)
      else controls.current.delete(`${key}:${control}`)
    }
  }

  function move(at: number, step: -1 | 1) {
    const to = at + step
    if (to < fixedCount || to >= draft.length) return
    const next = [...draft]
    next[at] = draft[to]
    next[to] = draft[at]
    refocus.current = { key: draft[at], control: step < 0 ? 'up' : 'down' }
    setDraft(next)
  }

  /** Ticking adds a column at the end, unticking takes it away. */
  function toggle(key: string, on: boolean) {
    refocus.current = { key, control: 'check' }
    setDraft((previous) => (on ? [...previous, key] : previous.filter((chosen) => chosen !== key)))
  }

  function apply() {
    onApply(draft)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" aria-describedby={undefined}>
        <SheetTitle className="text-lg">Kolommen</SheetTitle>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <ul className="flex flex-col">
            {draft.map((key, at) => {
              const property = shown.find((candidate) => candidate.key === key)
              if (!property) return null
              const fixed = property.column?.fixed === true
              return (
                <li key={key} className="flex items-center gap-1">
                  <label className="flex min-h-8 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted max-md:min-h-11">
                    <input
                      ref={controlRef(key, 'check')}
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      aria-label={property.label}
                      checked
                      disabled={fixed}
                      onChange={() => toggle(key, false)}
                    />
                    <span className="min-w-0 truncate">{property.label}</span>
                    {fixed ? <span className="text-muted-foreground">vast</span> : null}
                  </label>
                  {fixed ? null : (
                    <>
                      <Button
                        ref={controlRef(key, 'up')}
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${property.label} omhoog`}
                        disabled={at <= fixedCount}
                        onClick={() => move(at, -1)}
                      >
                        <ArrowUp />
                      </Button>
                      <Button
                        ref={controlRef(key, 'down')}
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${property.label} omlaag`}
                        disabled={at === draft.length - 1}
                        onClick={() => move(at, 1)}
                      >
                        <ArrowDown />
                      </Button>
                    </>
                  )}
                </li>
              )
            })}
          </ul>
          {others.length > 0 ? (
            <div className="flex flex-col gap-1">
              <h3 className="px-2 text-sm font-semibold">Niet getoond</h3>
              <ul className="flex flex-col">
                {others.map((property) => (
                  <li key={property.key}>
                    <label className="flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted max-md:min-h-11">
                      <input
                        ref={controlRef(property.key, 'check')}
                        type="checkbox"
                        className="size-4 shrink-0 accent-primary"
                        aria-label={property.label}
                        checked={false}
                        onChange={() => toggle(property.key, true)}
                      />
                      <span className="min-w-0 truncate">{property.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <Button type="button" variant="ghost" onClick={() => setDraft(draftOf(register.default_columns, register))}>
            Standaard herstellen
          </Button>
          <Button type="button" onClick={apply}>
            Toepassen
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
